import { BehaviorSubject, forkJoin, of } from "rxjs";
import { Injectable } from "@angular/core";
import { filter, switchMap } from "rxjs/operators";
import { NavigationEnd, Router } from "@angular/router";
import { LayoutConfigStorageService } from "./layout-config-storage.service";
import { LayoutConfig, LayoutConfigModel } from "../../config/layout";
import { TenantConfigurationService } from "./client-tenant-configuration/tenant-config.service";
import { TenantSynchronizationService } from "./tenants-synchronization.service";
import { USE_NEW_TRANSPORT_FLOW } from "./helpers/local-storage-keys";
import { ClientTenantConfigurationService } from "./swagger-gen/fordesk";

@Injectable({ providedIn: "root" })
export class LayoutConfigService {
  public layoutConfig: LayoutConfig;
  public onLayoutConfigUpdated$: BehaviorSubject<LayoutConfig>;

  // load logo only once per SPA lifetime
  private logoLoaded: boolean = false;
  private cachedLogoVersion: string | null = null;
  private cachedLogoDataUrl: string | null = null;

  constructor(
    private router: Router,
    private layoutConfigStorageService: LayoutConfigStorageService,
    private clientTenantConfigService: TenantConfigurationService,
    private tenantSynchronizationService: TenantSynchronizationService,
    private clientTenantConfigurationService: ClientTenantConfigurationService
  ) {
    // default config
    this.layoutConfig = new LayoutConfig();
    // register on config changed event and set default config
    this.onLayoutConfigUpdated$ = new BehaviorSubject(this.layoutConfig);

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        filter(() => this.tenantSynchronizationService.getCurrentTenant != null),
        switchMap(() => {
          return forkJoin({
            clientTenantConfig: this.clientTenantConfigService.initialize(),
            layoutConfig: this.layoutConfigStorageService.loadConfig(),
            // load logo version only once; later re-use cached value
            logoVersion: this.logoLoaded
              ? of(this.cachedLogoVersion ?? "")
              : this.clientTenantConfigurationService.getLogoVersion()
          });
        })
      )
      .subscribe(configs => {
        const layoutConfig: LayoutConfig = configs ? configs.layoutConfig : null;

        localStorage.setItem(
          USE_NEW_TRANSPORT_FLOW,
          `${configs.clientTenantConfig?.operationalFileSettings?.enableNewTransportFlow ?? false}`
        );

        const model = layoutConfig?.config as LayoutConfigModel;
        const version = (configs.logoVersion || "").trim();

        if (model?.header?.self?.logo) {
          if (this.logoLoaded) {
            // logo is already loaded once, just re-apply to current layout
            model.header.self.logo.light = this.cachedLogoDataUrl as any;
          } else {
            if (version) {
              this.clientTenantConfigurationService.getLogo(version)
                .subscribe(blob => {
                  const reader = new FileReader();
                  reader.onload = () => {
                    const dataUrl = reader.result as string;

                    // cache for next navigations
                    this.logoLoaded = true;
                    this.cachedLogoVersion = version;
                    this.cachedLogoDataUrl = dataUrl;

                    model.header.self.logo.light = dataUrl as any;

                    this.layoutConfig = layoutConfig;
                    this.onLayoutConfigUpdated$.next(layoutConfig);
                  };
                  reader.readAsDataURL(blob);
                });

              // temporary value before logo is loaded
              model.header.self.logo.light = null as any;
            } else {
              // no logo version -> no logo
              this.logoLoaded = true;
              this.cachedLogoVersion = "";
              this.cachedLogoDataUrl = null;
              model.header.self.logo.light = null as any;
            }
          }
        }

        this.layoutConfig = layoutConfig;
        this.onLayoutConfigUpdated$.next(layoutConfig);
      });
  }

  /**
   * Reset existing configurations
   * NOTE: This method will remove older config and pass only new;
   * @param model
   * @param doNotSave
   */
  public setModel(model: any, doNotSave?: boolean): void {
    // merge and replace existing config object
    // deep merge for mutltidimentional arrays
    this.layoutConfig = Object.assign({}, this.layoutConfig, model);

    if (!doNotSave) {
      this.layoutConfigStorageService.saveConfig(this.layoutConfig);
    }

    // fire off an event that all subscribers will listen
    this.onLayoutConfigUpdated$.next(this.layoutConfig);
  }

  public reloadSavedConfig(): void {
    this.setModel(new LayoutConfig(this.getSavedConfig()), true);
  }

  /**
   * Set current config as default template.
   * This config is changeable via layout builder.
   * Useful when want to reset layout without clearing the config at layout
   */
  public getSavedConfig() {
    return this.layoutConfigStorageService.loadConfig();
  }
}
